import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DirectionE, LangE } from "../../models/enums";

/** Shared by the invitation popup body and its footer - they are separate element trees. */
export type RegistrationInviteStateT = {
  token: string;
  phoneNumber: string;
  isEmailValid: boolean;
  isPhoneValid: boolean;
};

const emptyRegistrationInvite: RegistrationInviteStateT = {
  token: '',
  phoneNumber: '',
  isEmailValid: false,
  isPhoneValid: false,
};

export interface AppStateI {
  direction: DirectionE,
  lang: LangE,
  metaData: {}
  translatedStrings: {
    en: {

    },
    he: {

    },
  },
  isLoading: boolean;
  isFormDirty: boolean;
  findCampaignStep: number;
  deliveryServiceStep: number;
  isDeliveryServicePaymentAvailable: boolean;
  houseNumber: {
    [key: string]: string;
  }; 
  isGAInitialized: boolean;
  // Spec 29 / handoff C11: the shared-project link's signed token (was anonymousUserId, a bare user id).
  shareToken: string;
  UserMailSharedProject: string;
  registrationInvite: RegistrationInviteStateT;
}

const initialState: AppStateI = {
  direction: DirectionE.LTR,
  lang: LangE.EN,
  metaData: {},
  translatedStrings: {
    en: {

    },
    he: {

    },
  },
  isLoading: false,
  isFormDirty: false,
  findCampaignStep: 1,
  deliveryServiceStep: 1,
  isDeliveryServicePaymentAvailable: false,
  houseNumber: {},
  isGAInitialized: false,
  shareToken: '',
  UserMailSharedProject: '',
  registrationInvite: emptyRegistrationInvite,
};

export const authSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    switchLang: (state, action: PayloadAction<LangE>) => {
      state.lang = action.payload;
      if (action.payload === LangE.HE) {
        state.direction = DirectionE.RTL
      } else {
        state.direction = DirectionE.LTR
      }
    },
    toggleLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsFormDirty: (state, action: PayloadAction<boolean>) => {
      state.isFormDirty = action.payload;
    },
    setTranslatedStrings: (state, action: PayloadAction<{ en: {}, he: {} }>) => {
      state.translatedStrings = action.payload;
    },
    setFindCampaignStep: (state, action: PayloadAction<number>) => {
      state.findCampaignStep = action.payload;
    },
    setDeliveryServiceStep: (state, action: PayloadAction<number>) => {
      state.deliveryServiceStep = action.payload;
    },
    setIsDeliveryServicePaymentAvailable: (state, action: PayloadAction<boolean>) => {
      state.isDeliveryServicePaymentAvailable = action.payload;
    },
    setHouseNum: (state, action: PayloadAction<{key: string, value: string}>) => {
      state.houseNumber[action.payload.key] = action.payload.value;
    },
    clearHouseNum: (state) => {
      state.houseNumber = {};
    },
    setIsGAInitialized: (state, action: PayloadAction<boolean>) => {
      state.isGAInitialized = action.payload;
    },
    setShareToken: (state, action: PayloadAction<string>) => {
      state.shareToken = action.payload;
    },
    setUserMailSharedProject: (state, action: PayloadAction<string>) => {
      state.UserMailSharedProject = action.payload;
    },
    setRegistrationInvite: (state, action: PayloadAction<Partial<RegistrationInviteStateT>>) => {
      state.registrationInvite = { ...state.registrationInvite, ...action.payload };
    },
    resetRegistrationInvite: (state) => {
      state.registrationInvite = emptyRegistrationInvite;
    },
  },
});

export const {
  switchLang,
  toggleLoading,
  setIsFormDirty,
  setTranslatedStrings,
  setFindCampaignStep,
  setDeliveryServiceStep,
  setIsDeliveryServicePaymentAvailable,
  setHouseNum,
  setIsGAInitialized,
  clearHouseNum,
  setShareToken,
  setUserMailSharedProject,
  setRegistrationInvite,
  resetRegistrationInvite,
} = authSlice.actions;

export default authSlice.reducer;
